import { BrandedProductId, PremiumProductId } from '@seek/hirer-products-shim';
import { useEffect } from 'react';

import {
  PREMIUM_UPSELL_DISPLAYED,
  PREMIUM_UPSELL_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { saveTrackingData } from 'src/app/shared/dataAnalytics/storage';
import { useProductSetFromCart } from 'src/app/shared/hooks/useProductSetFromCart';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { getPremiumUpgradePrice } from 'src/app/utils/getPremiumUpgradePrice';
import { PremiumUpsell } from 'src/app/v2/components/PremiumUpsell';
import { useAnalyticsTracking } from 'src/app/v2/hooks/useAnalyticsTracking';
import { useHirerAccount } from 'src/app/v2/hooks/useHirerAccount';
import { useQueryParam } from 'src/app/v2/hooks/useQueryParam';
import type { CreateJobMutation } from 'src/graphql/generated';

interface UpsellSectionProps {
  job: NonNullable<CreateJobMutation['createJob']>;
}

export const PremiumUpsellSection: React.FC<UpsellSectionProps> = ({ job }) => {
  const { trackingAdapter } = useAnalyticsTracking();
  const {
    cartState: { items = [] } = {},
    addLineItem,
    assignJobIdAndRevisionToParentProduct,
    cartProductInProgress,
  } = useShoppingCart();
  const draftId = useQueryParam('draftid');
  const productSet = useProductSetFromCart({ draftId });
  const hirerAccount = useHirerAccount();
  const { price, tax } = getPremiumUpgradePrice({
    productSet,
    hidePrice: hirerAccount?.hidePrice,
  });
  const { jobId, revisionId } = job;
  const isPremiumSelected = Boolean(
    items.find((item) => item.product.id === PremiumProductId),
  );

  // @ts-ignore
  const isJobIdPresent = items.find((item) => Boolean(item.product?.job?.id));

  useEffect(() => {
    const eventData = {
      eventName: PREMIUM_UPSELL_DISPLAYED,
      adBudgetType: 'premiumUpsell',
    };
    trackingAdapter.sendLinkEvent(eventData);
    saveTrackingData({ isPremiumAdOffered: true });
  }, [trackingAdapter]);

  useEffect(() => {
    // This is a side effect of shopping cart not allowing job ID to be set before cart is updated
    // When cart is updated, job ID will be missing because a new product added
    if (!isJobIdPresent) {
      assignJobIdAndRevisionToParentProduct({
        jobId,
        revisionId,
      });
    }
  }, [
    isJobIdPresent,
    jobId,
    revisionId,
    assignJobIdAndRevisionToParentProduct,
  ]);

  const onButtonClicked = () => {
    const eventData = {
      eventName: PREMIUM_UPSELL_PRESSED,
      linkAction: isPremiumSelected ? 'remove' : 'select',
      premiumUpgradePrice: price,
    };

    trackingAdapter.sendLinkEvent(eventData);

    addLineItem({
      productId: isPremiumSelected ? BrandedProductId : PremiumProductId,
      productType: 'AdProduct',
    });
  };

  return (
    <PremiumUpsell
      price={price}
      tax={tax || undefined}
      onButtonClicked={onButtonClicked}
      selected={isPremiumSelected}
      loading={Boolean(Object.keys(cartProductInProgress).length)}
    />
  );
};
